import { logErrorToService } from "../../error-boundary/logerror";
import { iTenant } from "../../interfaces/auth";
import { showHideLoader } from "../../utilities/loading";
import { getSessionValue } from "../../utilities/localStore/calls";
import { sleep } from "../../utilities/methods";
import { UpdateAuthTokens } from "../Auth/TokenManager";

export interface iEmsApiResponse {
    isOk: boolean;
    status: number;
    statusText: string;
    validationErrors?: iValidationError | undefined;
    serverError?: iError | undefined;
    errorLogID?: string | undefined;
}

export interface iEmsApiResponseBody<TResponse> extends iEmsApiResponse {
    data?: TResponse | undefined;
    blob?: Blob;
    fileName?: string;
}

export interface iValidationError {
    title: string;
    errors: {
        [key: string]: string[];
    };
}

export interface iError {
    type: string;
    title: string;
    status: number;
    traceId: string;
}

export async function CallEmsApi<TResponse>(
    method: "GET" | "POST" | "PUT" | "DELETE",
    url: string,
    body?: any,
    headers?: Headers | undefined,
    quiet: boolean = false, // no loader, alert or error log: for calls whose failure the caller expects and handles
): Promise<iEmsApiResponseBody<TResponse>> {
    if (!quiet) {
        showHideLoader("flex");
    }

    let tenantToken = getSessionValue<iTenant>("tenant");
    const isInvalid = (token?: iTenant | undefined | null) => {
        if (!token) {
            return true;
        }

        const expiresIn = new Date(token.expiresAt).getTime() - new Date().getTime();

        return expiresIn < 1000;
    };

    while (isInvalid(tenantToken)) {
        await sleep(1000);
        tenantToken = getSessionValue<iTenant>("tenant");
    }

    const emsResponse: iEmsApiResponseBody<TResponse> = {
        isOk: false,
        status: 0,
        statusText: "",
        data: undefined,
        validationErrors: undefined,
        serverError: undefined,
        errorLogID: undefined,
    };

    let sURL = process.env.REACT_APP_API;
    if (!sURL || sURL.length === 0) {
        var str = window.location;
        let stringtoReplaces = str.origin.substring(
            str.protocol.length + 2,
            str.origin.indexOf(".", str.protocol.length + 2),
        );
        sURL = str.origin.replace(stringtoReplaces, "api");
    }
    if (!sURL.endsWith("/")) {
        sURL += "/";
    }

    sURL = sURL + url;

    if (headers === undefined) {
        headers = new Headers();
    }
    headers.append("Authorization", "bearer " + tenantToken?.token);

    if (body) {
        headers.append("Content-Type", "application/json");
    }

    var fetchOptions: any = {};
    fetchOptions.method = method;
    fetchOptions.headers = headers;
    if (body) {
        fetchOptions.body = JSON.stringify(body);
    }
    var response: Response = {} as Response;
    try {
        response = await fetch(sURL, fetchOptions);
    } catch (err) {
        emsResponse.isOk = false;
        emsResponse.status = 500;
        emsResponse.statusText = "Failed to fetch";
        emsResponse.serverError = {
            type: "https://tools.ietf.org/html/rfc7231#section-6.5.4",
            title: "Failed to fetch",
            status: 500,
            traceId: "",
        };
        console.log("err", err);
        return emsResponse;
    }
    if (response.status === 401) {
        await UpdateAuthTokens();
        // the refresh replaced the tenant token, so the retry must not reuse the stale Authorization header
        headers.set("Authorization", "bearer " + getSessionValue<iTenant>("tenant")?.token);
        response = await fetch(sURL, fetchOptions);
    }

    emsResponse.isOk = response.ok;
    emsResponse.status = response.status;
    emsResponse.statusText = response.statusText;
    if (quiet) {
        if (response.ok && response.status === 200) {
            emsResponse.data = await response.json();
        }
        return emsResponse;
    }
    let errorMessage = "";
    if (response.ok && response.status === 200) {
        emsResponse.data = await response.json();
    } else if (response.status === 400) {
        emsResponse.validationErrors = await response.json();
        errorMessage = `400 - ${JSON.stringify(emsResponse.validationErrors)}`;
        emsResponse.errorLogID = logErrorToService(new Error(errorMessage));
        errorMessage += `\n\n errorLogid: ${emsResponse.errorLogID}`;
        alert(errorMessage);
    } else if (response.status === 500) {
        emsResponse.serverError = await response.json();
        errorMessage = `500 - ${JSON.stringify(emsResponse.serverError)}`;
        emsResponse.errorLogID = logErrorToService(new Error(errorMessage));
        errorMessage += `\n\n errorLogid: ${emsResponse.errorLogID}`;
        alert(errorMessage);
    } else if (!response.ok) {
        errorMessage = `${response.status} - ${response.statusText}`;
        emsResponse.errorLogID = logErrorToService(new Error(errorMessage));
        errorMessage += `\n\n errorLogid: ${emsResponse.errorLogID}`;
        alert(errorMessage);
    }

    showHideLoader("none");
    return emsResponse;
}
export async function CallEmsApiForBlob<TResponse>(
    method: "GET" | "POST" | "PUT" | "DELETE",
    url: string,
    body?: any,
): Promise<iEmsApiResponseBody<TResponse>> {
    showHideLoader("flex");

    let tenantToken = getSessionValue<iTenant>("tenant");
    const isInvalid = (token?: iTenant | undefined | null) => {
        if (!token) {
            return true;
        }

        const expiresIn = new Date(token.expiresAt).getTime() - new Date().getTime();

        return expiresIn < 1000;
    };

    while (isInvalid(tenantToken)) {
        await sleep(1000);
        tenantToken = getSessionValue<iTenant>("tenant");
    }

    const emsResponse: iEmsApiResponseBody<TResponse> = {
        isOk: false,
        status: 0,
        statusText: "",
        data: undefined,
        validationErrors: undefined,
        serverError: undefined,
        errorLogID: undefined,
    };

    let sURL = process.env.REACT_APP_API;
    if (!sURL || sURL.length === 0) {
        var str = window.location;
        let stringtoReplaces = str.origin.substring(
            str.protocol.length + 2,
            str.origin.indexOf(".", str.protocol.length + 2),
        );
        sURL = str.origin.replace(stringtoReplaces, "api");
    }
    if (!sURL.endsWith("/")) {
        sURL += "/";
    }

    sURL = sURL + url;

    const headers = new Headers();
    headers.append("Authorization", "bearer " + tenantToken?.token);

    if (body) {
        headers.append("Content-Type", "application/json");
    }

    var fetchOptions: any = {};
    fetchOptions.method = method;
    fetchOptions.headers = headers;
    if (body) {
        fetchOptions.body = JSON.stringify(body);
    }

    var response = await fetch(sURL, fetchOptions);
    if (response.status === 401) {
        await UpdateAuthTokens();
        // the refresh replaced the tenant token, so the retry must not reuse the stale Authorization header
        headers.set("Authorization", "bearer " + getSessionValue<iTenant>("tenant")?.token);
        response = await fetch(sURL, fetchOptions);
    }

    emsResponse.isOk = response.ok;
    emsResponse.status = response.status;
    emsResponse.statusText = response.statusText;
    let errorMessage = "";
    if (response.ok && response.status === 200) {
        var temp = response;
        emsResponse.blob = await temp.blob();
        var contentDisposition = response.headers.get("Content-Disposition");
        if (contentDisposition) emsResponse.fileName = contentDisposition.split('"')[1];
    } else if (response.status === 400) {
        emsResponse.validationErrors = await response.json();
        errorMessage = `400 - ${JSON.stringify(emsResponse.validationErrors)}`;
        emsResponse.errorLogID = logErrorToService(new Error(errorMessage));
        errorMessage += `\n\n errorLogid: ${emsResponse.errorLogID}`;
        alert(errorMessage);
    } else if (response.status === 500) {
        emsResponse.serverError = await response.json();
        errorMessage = `500 - ${JSON.stringify(emsResponse.serverError)}`;
        emsResponse.errorLogID = logErrorToService(new Error(errorMessage));
        errorMessage += `\n\n errorLogid: ${emsResponse.errorLogID}`;
        alert(errorMessage);
    } else if (!response.ok) {
        errorMessage = `${response.status} - ${response.statusText}`;
        emsResponse.errorLogID = logErrorToService(new Error(errorMessage));
        errorMessage += `\n\n errorLogid: ${emsResponse.errorLogID}`;
        alert(errorMessage);
    }

    showHideLoader("none");
    return emsResponse;
}
